import { Tabs, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { AdminRoutesPaths } from "../../../../../constants/admin-routes"
import { RoutesPaths } from "../../../../../constants/user-routes"
import Icon from "../../../../../Icon"
import { RECEIPT_CONFIGURE_SLUGS } from "../../../constants/receipt-configure-slugs"
import { OVERLAY_STATUS_COPY } from "../ProfileStatusOverlay/ProfileStatusOverlay"

import styles from "./configureTabs.module.less"

const { Text } = Typography

export const RECEIPT_CONFIGURE_TABS = {
  PROFILE: "profile",
  BUSINESS: "business"
}

// `adminHref` is the same tab under Business settings, where support browses a
// merchant's Configure (CHA1-1975). `lockOnOverlayStatus` describes a
// merchant-side rule only — see the locking note in ConfigureTabs.
//
// There is deliberately no BA-role gate here: every role opens Profile, and
// Manager / View only get it read-only the way support does (CHA1-2981). The
// read-only wiring lives in the page itself (see `readOnly` in ../../index.jsx).
const CONFIGURE_TABS_CONFIG = [
  {
    key: RECEIPT_CONFIGURE_TABS.PROFILE,
    href: RoutesPaths.receipts.configure_profile_hash(RECEIPT_CONFIGURE_SLUGS.business_info),
    adminHref: AdminRoutesPaths.receipts.configure_profile_hash(
      RECEIPT_CONFIGURE_SLUGS.business_info
    ),
    label: "Profile",
    lockOnOverlayStatus: true,
    testId: "deflect-configure-tab-profile"
  },
  {
    key: RECEIPT_CONFIGURE_TABS.BUSINESS,
    href: RoutesPaths.receipts.configure_business,
    adminHref: AdminRoutesPaths.receipts.configure_business,
    label: "Business",
    testId: "deflect-configure-tab-business"
  }
]

// `overlayStatus` is optional: it's the same server-driven lock ConfigureProfilePage
// derives from its wizard (see overlayStatusFromView) - passed in by whichever page
// currently has it loaded, since Configure state isn't guaranteed hydrated on every
// route that renders these tabs.
//
// `usersViewForAdmin` switches the tabs to support's copy of the page under Business
// settings (CHA1-1975). Two things change there: the hrefs point at the admin routes
// and carry the current `search` (the BA is a `business_account_id` query param, so
// dropping it would lose the account context), and the lock does not apply - it
// exists to stop a merchant editing a submitted profile, while support is read-only
// on every step regardless. The lock itself is role-blind: an in-review profile is
// closed to every merchant role, Owner included.
const ConfigureTabs = ({ activeTab, overlayStatus, usersViewForAdmin }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()

  const tabsByKey = Object.fromEntries(CONFIGURE_TABS_CONFIG.map((tab) => [tab.key, tab]))

  const items = CONFIGURE_TABS_CONFIG.map(({ key, label, testId, lockOnOverlayStatus }) => {
    const disabled = !usersViewForAdmin && lockOnOverlayStatus && !!overlayStatus

    return {
      key,
      disabled,
      label: disabled ? (
        <Tooltip title={t(OVERLAY_STATUS_COPY[overlayStatus]?.title)}>
          <Text type="secondary" data-testid={testId} className={styles.disabledLabel}>
            {t(label)}
            <Icon type="lock" className={styles.lockIcon} />
          </Text>
        </Tooltip>
      ) : (
        <span data-testid={testId}>{t(label)}</span>
      )
    }
  })

  return (
    <Tabs
      className={styles.tabs}
      activeKey={activeTab}
      onChange={(key) => {
        const tab = tabsByKey[key]
        if (!tab) return
        if (usersViewForAdmin) {
          navigate({ pathname: tab.adminHref, search })
          return
        }
        if (tab.lockOnOverlayStatus && overlayStatus) return
        navigate(tab.href)
      }}
      items={items}
    />
  )
}

export default ConfigureTabs
